/**
 * Чип интенсивности на карточке плана (P0.2 P4).
 *
 * ═══ ОДНА ШКАЛА, ДВЕ ПОДРОБНОСТИ — 2026-08-03 ═══
 *
 * Здесь стояла САМОСТОЯТЕЛЬНАЯ шкала: три ступени по абсолютному TSS
 * (>120 HIGH, >60 MED, иначе LOW), без хронической нагрузки. Рядом жила
 * вторая — getDifficulty, четыре ступени по доле от CTL. Одна тренировка
 * получала два ответа, и чем слабее атлет, тем сильнее они расходились:
 * границы совпадали только при CTL ≈ 110-130, то есть у профессионала.
 * Ниже — а это почти все — карточка занижала на одну-две ступени.
 *
 * Теперь ответ один. Уровень 1..4 считает getDifficulty (зеркало
 * shared/services/workout_difficulty.py, оно же у тренера в разборе), а чип
 * схлопывает его в три ступени ПРИСУТСТВИЯ. Схлопывание — не потеря
 * точности, а разная подробность одного ответа: карточка отвечает «насколько
 * заметен день в ряду», деталка — «насколько он тяжёл именно тебе», и цифра
 * процента от CTL живёт там же.
 *
 * Почему чип не стал четырёхступенчатым: его различение построено на трёх
 * уровнях светлоты (см. ниже, ΔL* 12.7-19.0 при пороге 5.0). Четвёртая
 * ступень потребовала бы нового канала — то есть переоткрытия калибровки
 * 29.07 ради ряда, который и так листают глазами, а не читают по одному.
 *
 * ═══ ЕДИНСТВЕННЫЙ ИСТОЧНИК ЦВЕТА ЧИПА — 2026-07-29 ═══
 *
 * До этой даты чип существовал дважды: здесь классами и в ListView.tsx
 * собственной таблицей `INTENSITY_HEX`, красившей инлайн-стилем. Копии
 * разошлись, как расходятся все копии, и разошлись хуже, чем оттенком:
 *   · HIGH брал --danger-500 напрямую, минуя разведение ролей 29.07 —
 *     3.57:1 на карточке тёмной темы там, где --bad-text даёт 5.21:1;
 *   · MED брал --warning-500 — янтарь на белой карточке, 2.12:1;
 *   · LOW брал --chart-1, а тот в тёмной теме = var(--active-500), лайм,
 *     выведенный канон-сессией 24.07 из роли статуса.
 *
 * Ни tsc, ни vitest, ни check_theme_contrast этого не видели: guard читает
 * ТОКЕНЫ семантического слоя, а инлайн `color: var(--danger-500)` идёт мимо
 * него. Проверять надо точку УПОТРЕБЛЕНИЯ, а не точку объявления.
 *
 * Отсюда правило: цвет чипа интенсивности берётся только здесь и только
 * классом. Класс резолвится через tailwind.config.js в роль-токен, поэтому
 * следующая калибровка шкалы дойдёт до чипа сама.
 */

import type { DifficultyLevel } from "../../workoutStatus";

export type Intensity = "HIGH" | "MED" | "LOW";

export interface IntensityBadge {
  level: Intensity;
  /**
   * Полный набор классов чипа — геометрия и внешний вид разом.
   *
   * Одна строка, а не пара «текст + подложка»: раздельные поля дают
   * представлениям возможность взять половину, и одно из них однажды берёт
   * не ту. Ровно так ListView и жил со своей таблицей цвета.
   */
  className: string;
}

/**
 * ═══ ПОЧЕМУ НЕ СВЕТОФОР — 2026-07-29 ═══
 *
 * До этой даты ступени красились ok / warn / bad, то есть система говорила
 * атлету: лёгкая тренировка зелёная и «хорошо», тяжёлая красная и «плохо».
 * Это неверно по методологии. Высокая интенсивность в ПЛАНЕ — не отклонение
 * и не опасность, а назначенная работа, ради которой план и составлен;
 * сигнальная палитра означает «ошибка / требует реакции» и здесь врёт.
 *
 * Тот же разбор уже проведён у зон формы (см. lib/formZones.ts): сигнальный
 * цвет там получает ровно одна зона из семи — та, что требует реакции,
 * остальные шесть описывают, а не судят. У интенсивности плана зоны,
 * требующей реакции, нет вообще: все три ступени — описание.
 *
 * Цвет ДИСЦИПЛИНЫ вместо светофора не подошёл, и это измерено, а не
 * прикинуто: как текст в светлой теме он даёт run 1.38:1, swim 2.01,
 * trail 2.16, bike 3.04 — все ниже AA ещё до всякой градации. Цвета
 * дисциплин заливочные, ровно как --accent и --bad до разведения ролей.
 *
 * Поэтому ступень несёт не оттенок, а ПРИСУТСТВИЕ — светлота текста плюс
 * плотность подложки: заливка → контур → ничего. Два канала различения
 * вместо одного, оба работают в ч/б и при дальтонизме, и ни одного нового
 * токена: шкала --fg-muted / --fg-secondary / --fg-primary уже выверена
 * против карточки (5.40 / 8.42 / 14.42 в тёмной, 7.92 / 13.19 / 18.10 в
 * светлой; ΔL* между ступенями 12.7–19.0 при пороге различимости 5.0).
 */
const CHIP =
  "inline-flex items-center rounded border px-1.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.18em]";

const VISUALS: Record<Intensity, IntensityBadge> = {
  // Инверсия — самая плотная форма: тяжёлый день виден, не крича цветом.
  HIGH: { level: "HIGH", className: `${CHIP} border-transparent bg-fg-primary text-bg-card` },
  // Контур: присутствие есть, веса меньше.
  MED:  { level: "MED",  className: `${CHIP} border-fg-secondary/40 text-fg-secondary` },
  // Ничего, кроме приглушённого текста. Рамка прозрачная — чтобы геометрия
  // всех трёх ступеней совпадала и ряд не дёргался по вертикали.
  LOW:  { level: "LOW",  className: `${CHIP} border-transparent text-fg-muted` },
};

/**
 * Уровень тяжести 1..4 → чип из трёх ступеней.
 *
 * Схлопывание: «Легко» → LOW, «Средне» и «Тяжело» → MED, «Жёстко» → HIGH.
 * Середина широкая намеренно — обе её ступени описывают назначенную работу
 * штатной недели, а различает их процент от CTL, который карточке негде
 * показать. Крайние ступени узкие: восстановление и перегрузка — это то, что
 * должно выделяться в ряду с одного взгляда.
 */
export function intensityChip(
  level: DifficultyLevel | null | undefined,
): IntensityBadge | null {
  if (level == null) return null;
  if (level >= 4) return VISUALS.HIGH;
  if (level >= 2) return VISUALS.MED;
  return VISUALS.LOW;
}
